import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MapPin, Building2, Globe, ExternalLink, CheckCircle2, ImageOff } from "lucide-react";
import { api, API_BASE } from "@/lib/api";

function fmt(dt) {
  if (!dt) return "—";
  return new Date(dt).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "medium" });
}

const statusMap = {
  on_time: { label: "Tepat Waktu", cls: "bg-signal-ok text-white" },
  late: { label: "Terlambat", cls: "bg-signal-late text-white" },
};

/**
 * Loads selfie image as a blob via Bearer auth; falls back gracefully if unavailable.
 */
function SelfieImage({ attendanceId, kind, hasSelfie }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let currentUrl = null;
    if (!hasSelfie) return;
    (async () => {
      try {
        const resp = await api.get(`/attendance/${attendanceId}/selfie/${kind}`, {
          responseType: "blob",
        });
        if (cancelled) return;
        currentUrl = URL.createObjectURL(resp.data);
        setBlobUrl(currentUrl);
      } catch (_e) {
        if (!cancelled) setErr(true);
      }
    })();
    return () => {
      cancelled = true;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [attendanceId, kind, hasSelfie]);

  if (!hasSelfie || err) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center text-white/60 text-sm gap-2">
        <ImageOff className="w-8 h-8" />
        <span>Foto tidak tersedia</span>
      </div>
    );
  }
  if (!blobUrl) {
    return <div className="w-full h-full flex items-center justify-center text-white/60 text-sm">Memuat foto...</div>;
  }
  return (
    <img
      data-testid={`detail-selfie-${kind}`}
      src={blobUrl}
      alt={kind}
      className="w-full h-full object-cover"
    />
  );
}

export default function AttendanceDetailDialog({ record, open, onClose }) {
  if (!record) return null;
  const s = statusMap[record.status] || { label: "-", cls: "bg-gray-200" };
  const gm = (lat, lng) =>
    lat != null && lng != null ? `https://www.google.com/maps?q=${lat},${lng}` : null;

  // Backend returns has_check_in_selfie flag (new) OR legacy inline check_in_selfie base64
  const hasIn = record.has_check_in_selfie ?? !!record.check_in_selfie;
  const hasOut = record.has_check_out_selfie ?? !!record.check_out_selfie;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-none max-w-4xl border-black/20 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">
            Detail Absensi — {record.user_name}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="col-span-full border border-black/10 bg-white p-4 grid grid-cols-2 sm:grid-cols-6 gap-3">
            <Meta label="Tanggal" value={record.date} />
            <Meta label="NPP" value={record.user_npp || <span className="text-gray-400">—</span>} />
            <Meta label="Unit Kerja" value={record.user_unit_name || <span className="text-gray-400">—</span>} />
            <Meta
              label="Lokasi"
              value={
                record.is_wfa ? (
                  <span className="inline-flex items-center gap-1 text-klein font-semibold">
                    <Globe className="w-4 h-4" /> WFA
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1">
                    <Building2 className="w-4 h-4" /> {record.office_name || "—"}
                  </span>
                )
              }
            />
            <Meta label="Email" value={record.user_email} />
            <Meta
              label="Status"
              value={
                <span className={`px-2 py-1 text-xs font-bold uppercase tracking-widest ${s.cls}`}>
                  {s.label}
                </span>
              }
            />
          </div>

          <PhotoBlock
            title="Check-in"
            time={record.check_in_time}
            lat={record.check_in_lat}
            lng={record.check_in_lng}
            distance={record.check_in_distance}
            attendanceId={record.attendance_id}
            kind="check_in"
            hasSelfie={hasIn}
            mapsUrl={gm(record.check_in_lat, record.check_in_lng)}
          />
          <PhotoBlock
            title="Check-out"
            time={record.check_out_time}
            lat={record.check_out_lat}
            lng={record.check_out_lng}
            distance={record.check_out_distance}
            attendanceId={record.attendance_id}
            kind="check_out"
            hasSelfie={hasOut}
            mapsUrl={gm(record.check_out_lat, record.check_out_lng)}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="label-tiny">{label}</div>
      <div className="font-semibold text-sm mt-1">{value}</div>
    </div>
  );
}

function PhotoBlock({ title, time, lat, lng, distance, attendanceId, kind, hasSelfie, mapsUrl }) {
  const missing = !time;
  return (
    <div className="border border-black/10 bg-white">
      <div className="px-4 py-3 border-b border-black/10 flex items-center justify-between">
        <div className="label-tiny">{title}</div>
        {!missing && <CheckCircle2 className="w-4 h-4 text-signal-ok" />}
      </div>
      {missing ? (
        <div className="p-8 text-center text-sm text-gray-400 min-h-[300px] flex items-center justify-center">
          Belum ada data {title.toLowerCase()}.
        </div>
      ) : (
        <>
          <div className="aspect-square bg-black flex items-center justify-center overflow-hidden">
            <SelfieImage attendanceId={attendanceId} kind={kind} hasSelfie={hasSelfie} />
          </div>
          <div className="p-4 space-y-2 text-sm">
            <Row label="Waktu" value={fmt(time)} />
            <Row
              label="Koordinat"
              value={
                lat != null && lng != null ? (
                  <span className="font-mono">
                    {Number(lat).toFixed(5)}, {Number(lng).toFixed(5)}
                  </span>
                ) : (
                  "—"
                )
              }
            />
            <Row
              label="Jarak ke kantor"
              value={distance != null ? `${distance} m` : <span className="text-klein">—</span>}
            />
            {mapsUrl && (
              <a
                href={mapsUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 mt-2 border border-black/20 px-3 py-1.5 hover:bg-black hover:text-white transition-colors text-xs font-semibold"
              >
                <MapPin className="w-3.5 h-3.5" /> Buka di Google Maps <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="text-xs text-gray-500 uppercase tracking-wider">{label}</div>
      <div className="font-semibold text-right">{value}</div>
    </div>
  );
}
